import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Mail, Lock, User, Shield, ChevronLeft, Phone, Smartphone, MapPin, Facebook, Droplet, Camera, IdCard, Eye, EyeOff, MessageSquare, KeyRound, Check } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import vsuLogo from '@/assets/vsu-logo.jpg';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { bangladeshDistricts, bloodGroups } from '@/data/bangladeshDistricts';
import { RequestPasswordHelp } from '@/components/RequestPasswordHelp';
import { SEOHead } from '@/components/SEOHead';
import { compressImage } from '@/lib/imageCompression';

const signInSchema = z.object({
  email: z.string().trim().email('Invalid email address').max(255, 'Email too long'),
  password: z.string().min(6, 'Password must be at least 6 characters').max(128, 'Password too long'),
});

const signUpSchema = z.object({
  fullName: z.string().trim().min(2, 'Name must be at least 2 characters').max(100),
  email: z.string().trim().email('Invalid email address').max(255),
  password: z.string().min(6, 'Password must be at least 6 characters').max(128),
  confirmPassword: z.string(),
  gameId: z.string().trim().min(1, 'Game ID is required').max(50),
  phone: z.string().regex(/^(\+?880)?[0-9]{10,11}$/, 'Invalid phone number').optional().or(z.literal('')),
  bloodGroup: z.string().optional(),
  mobileDevice: z.string().max(100).optional(),
  district: z.string().optional(),
  facebookUrl: z.string().url('Invalid Facebook URL').optional().or(z.literal('')),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});


const resetPasswordSchema = z.object({
  newPassword: z.string().min(6, 'Password must be at least 6 characters').max(128, 'Password too long'),
  confirmNewPassword: z.string(),
}).refine((data) => data.newPassword === data.confirmNewPassword, {
  message: "Passwords don't match",
  path: ["confirmNewPassword"],
});

type SignInFormValues = z.infer<typeof signInSchema>;
type SignUpFormValues = z.infer<typeof signUpSchema>;

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export default function AuthPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('signin');
  
  const [showAdminHelpRequest, setShowAdminHelpRequest] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showSignInPassword, setShowSignInPassword] = useState(false);
  const [showSignUpPassword, setShowSignUpPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { signIn, signUp, user, isAdmin, isEditor } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const from = (location.state as { from?: string })?.from || '/';

  // Component mount logging
  useEffect(() => {
    console.log('🚀 Auth Page Mounted');
    console.log('🚀 Full URL:', window.location.href);
    console.log('🚀 Hash:', window.location.hash);
    console.log('🚀 Search:', window.location.search);
    
    if (window.location.hash) {
      console.log('🚀 Hash fragment detected - may contain auth tokens');
    }
  }, []);

  // Detect password reset URL parameter
  useEffect(() => {
    console.log('🔍 Auth Page - Checking URL parameters');
    console.log('🔍 Current URL:', window.location.href);
    console.log('🔍 Search params:', location.search);
    
    const params = new URLSearchParams(location.search);
    const resetParam = params.get('reset');
    console.log('🔍 Reset param value:', resetParam);
    
    if (resetParam === 'true') {
      console.log('✅ Reset mode activated - showing new password form');
      setShowResetPassword(true);
    }
  }, [location.search]);

  useEffect(() => {
    if (user && !showResetPassword) {
      // Redirect admins/editors to admin panel, players to dashboard
      if (isAdmin || isEditor) {
        navigate('/admin/settings', { replace: true });
      } else {
        // Regular players go to their dashboard
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, isAdmin, isEditor, navigate, showResetPassword]);

  const signInForm = useForm<SignInFormValues>({
    resolver: zodResolver(signInSchema),
    defaultValues: { email: '', password: '' },
  });


  const resetPasswordForm = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { newPassword: '', confirmNewPassword: '' },
  });

  const signUpForm = useForm<SignUpFormValues>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { 
      fullName: '', 
      email: '', 
      password: '', 
      confirmPassword: '',
      gameId: '',
      phone: '',
      bloodGroup: '',
      mobileDevice: '',
      district: '',
      facebookUrl: '',
    },
  });

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Avatar image must be less than 5MB',
          variant: 'destructive',
        });
        return;
      }
      setAvatarFile(file);
      setAvatarError(null); // Clear error when file is selected
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadAvatar = async (userId: string): Promise<string | null> => {
    if (!avatarFile) return null;
    
    setIsUploadingAvatar(true);
    try {
      // Compress image before upload to improve LCP
      const compressedFile = await compressImage(avatarFile, 400, 400, 0.8, 200);
      const filePath = `${userId}/${Date.now()}.jpg`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, compressedFile);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Avatar upload error:', error);
      return null;
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const onSignIn = async (values: SignInFormValues) => {
    setIsLoading(true);
    
    // If "Remember Me" is unchecked, we'll clear the session when browser closes
    // by storing a flag that the app can check
    if (!rememberMe) {
      sessionStorage.setItem('vsu_session_only', 'true');
    } else {
      sessionStorage.removeItem('vsu_session_only');
    }
    
    const { error, isAdmin: isAdminUser } = await signIn(values.email.trim(), values.password);
    setIsLoading(false);

    if (error) {
      toast({
        title: 'Sign in failed',
        description: error.message === 'Invalid login credentials' 
          ? 'Invalid email or password. Please try again.' 
          : error.message,
        variant: 'destructive',
      });
    } else {
      toast({ title: 'Welcome back!' });
      // Navigate to admin settings if admin/editor, players to dashboard
      if (isAdminUser) {
        navigate('/admin/settings', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  };

  const onSignUp = async (values: SignUpFormValues) => {
    // Validate avatar is uploaded
    if (!avatarFile) {
      setAvatarError('Profile picture is required');
      toast({
        title: 'Profile picture required',
        description: 'Please upload a profile picture to complete registration.',
        variant: 'destructive',
      });
      return;
    }
    
    setIsLoading(true);
    const { error } = await signUp(values.email.trim(), values.password, values.fullName.trim());
    
    if (error) {
      setIsLoading(false);
      toast({
        title: 'Sign up failed',
        description: error.message.includes('already registered')
          ? 'This email is already registered. Please sign in instead.'
          : error.message,
        variant: 'destructive',
      });
      return;
    }

    // Wait for session to be fully established before uploading avatar
    // This ensures auth.uid() is available in RLS policies
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Refresh session to ensure it's valid
    const { data: sessionData } = await supabase.auth.refreshSession();
    
    // Get the current user after signup
    const { data: { user: newUser } } = await supabase.auth.getUser();
    
    if (newUser && sessionData?.session) {
      let avatarUrl: string | null = null;
      if (avatarFile) {
        avatarUrl = await uploadAvatar(newUser.id);
      }

      // Upsert profile (in case trigger didn't create it)
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: newUser.id,
          email: values.email.trim(),
          full_name: values.fullName.trim(),
          game_id: values.gameId.trim(),
          phone: values.phone?.trim() || null,
          blood_group: values.bloodGroup || null,
          mobile_device: values.mobileDevice?.trim() || null,
          district: values.district || null,
          facebook_url: values.facebookUrl?.trim() || null,
          avatar_url: avatarUrl,
        }, { onConflict: 'id' });

      if (profileError) {
        console.error('Profile upsert error:', profileError);
      }

      // Auto-create PENDING player entry - admin approval required before public visibility
      const generateSlug = (name: string) => 
        name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') + '-' + Date.now().toString(36);

      let playerCreated = false;
      let retryCount = 0;
      const maxRetries = 3;

      while (!playerCreated && retryCount < maxRetries) {
        const { error: playerError } = await supabase
          .from('players')
          .insert({
            name: values.fullName.trim(),
            slug: generateSlug(values.fullName.trim()),
            avatar: avatarUrl,
            profile_id: newUser.id,
            is_active: true,
            approval_status: 'pending',
            stats: { matches: 0, goals: 0, assists: 0, wins: 0, draws: 0, losses: 0, points: 0 }
          });

        if (playerError) {
          console.error(`Player creation attempt ${retryCount + 1} error:`, playerError);
          retryCount++;
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } else {
          playerCreated = true;
        }
      }

      if (!playerCreated) {
        console.error('Failed to create player after all retries');
        // Still continue - the player can be created manually by admin
      }

      // Insert user role - always insert 'user' role first, then check if database trigger
      // updated it to 'admin' for master admin email
      const { error: roleError } = await supabase
        .from('user_roles')
        .insert({
          user_id: newUser.id,
          role: 'user'
        });

      if (roleError && !roleError.message.includes('duplicate')) {
        console.error('Role insert error:', roleError);
      }

      // Fetch the actual role from database to determine redirect
      // This ensures we use server-side role assignment, not client-side email checks
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', newUser.id)
        .maybeSingle();

      const userRole = roleData?.role;
      const isAdminOrEditor = userRole === 'admin' || userRole === 'editor';

      setIsLoading(false);
      toast({
        title: 'Registration Successful!',
        description: 'Your account has been created. An admin will review your profile shortly.',
      });
      
      // Redirect based on fetched role from server
      navigate(isAdminOrEditor ? '/admin/settings' : '/dashboard', { replace: true });
      return;
    }

    setIsLoading(false);
    toast({
      title: 'Registration Successful!',
      description: 'Your account has been created. An admin will review your profile shortly.',
    });
    navigate('/dashboard', { replace: true });
  };


  const onResetPassword = async (values: ResetPasswordFormValues) => {
    console.log('🔐 Reset Password - Starting process');
    
    // Check current session state
    const { data: sessionData } = await supabase.auth.getSession();
    console.log('🔐 Current session:', sessionData);
    console.log('🔐 Session user:', sessionData?.session?.user?.email);
    console.log('🔐 Access token exists:', !!sessionData?.session?.access_token);
    
    setIsLoading(true);
    
    console.log('🔐 Calling supabase.auth.updateUser...');
    const { data, error } = await supabase.auth.updateUser({
      password: values.newPassword,
    });
    
    console.log('🔐 Update response data:', data);
    console.log('🔐 Update response error:', error);
    
    setIsLoading(false);

    if (error) {
      console.log('❌ Reset Password - Error:', error.message);
      toast({
        title: 'Failed to reset password',
        description: error.message,
        variant: 'destructive',
      });
    } else {
      console.log('✅ Reset Password - Success');
      toast({
        title: 'Password reset successful!',
        description: 'You can now sign in with your new password.',
      });
      setShowResetPassword(false);
      // Clear URL parameter
      navigate('/auth', { replace: true });
      setActiveTab('signin');
    }
  };

  return (
    <>
      <SEOHead 
        title="Sign In | Viper Strike United" 
        description="Sign in or create an account to join Viper Strike United eFootball gaming community. Access your player dashboard and compete in tournaments."
      />
      <div className="min-h-screen flex items-center justify-center hero-gradient relative overflow-hidden p-4">
      {/* Background Effects */}
      <div className="absolute inset-0 hexagon-pattern pointer-events-none"></div>
      <div className="absolute inset-0 grid-pattern pointer-events-none"></div>
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] glow-gradient opacity-30 pointer-events-none"></div>
      
      {/* Animated Scan Line */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent animate-scan opacity-20"></div>
      </div>

      {/* Back Button */}
      <Link 
        to="/" 
        className="absolute top-6 left-6 flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors z-20"
      >
        <ChevronLeft className="h-5 w-5" />
        <span className="text-sm font-medium">Back to Home</span>
      </Link>

      <Card className="w-full max-w-lg relative z-10 bg-card/90 backdrop-blur-xl border-border/50 gaming-border max-h-[90vh] overflow-y-auto overscroll-contain">
        <div className="absolute top-0 left-0 right-0 h-1 neon-gradient rounded-t-xl"></div>
        
        <CardHeader className="text-center pb-4">
          <Link to="/" className="flex items-center justify-center gap-3 mb-4 group">
            <img 
              src={vsuLogo} 
              alt="Viper Strike United Logo" 
              className="h-12 w-12 rounded-xl object-cover ring-2 ring-primary/20 group-hover:ring-primary/50 transition-shadow"
            />
          </Link>
          <CardTitle className="font-display text-2xl tracking-wider">
            <span className="text-foreground">Viper Strike</span>{' '}
            <span className="text-gradient">United</span>
          </CardTitle>
          <CardDescription className="text-muted-foreground text-sm">
            eFootball Mobile Gaming Community
          </CardDescription>
        </CardHeader>

        <CardContent>
          {/* Reset Password Form - Shown when user clicks reset link from email */}
          {showResetPassword ? (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                  <KeyRound className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground">Set New Password</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Enter your new password below
                </p>
              </div>

              <Form {...resetPasswordForm}>
                <form onSubmit={resetPasswordForm.handleSubmit(onResetPassword)} className="space-y-4">
                  <FormField
                    control={resetPasswordForm.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">New Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type={showNewPassword ? "text" : "password"} 
                              placeholder="••••••••" 
                              className="pl-10 pr-10 bg-muted/50 border-border/50 focus:border-primary" 
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowNewPassword(!showNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={resetPasswordForm.control}
                    name="confirmNewPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">Confirm New Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type={showConfirmNewPassword ? "text" : "password"} 
                              placeholder="••••••••" 
                              className="pl-10 pr-10 bg-muted/50 border-border/50 focus:border-primary" 
                              autoComplete="new-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showConfirmNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button 
                    type="submit" 
                    className="w-full btn-gaming h-12 text-background font-semibold tracking-wider" 
                    disabled={isLoading}
                  >
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    RESET PASSWORD
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowResetPassword(false);
                      navigate('/auth', { replace: true });
                    }}
                    className="w-full"
                  >
                    Back to Sign In
                  </Button>
                </form>
              </Form>
            </div>
          ) : (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-2 mb-6 bg-muted/50 relative z-20">
              <TabsTrigger 
                value="signin" 
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-semibold cursor-pointer select-none touch-manipulation"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger 
                value="signup"
                className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground font-semibold cursor-pointer select-none touch-manipulation"
              >
                Sign Up
              </TabsTrigger>
            </TabsList>

            <TabsContent value="signin">
              <Form {...signInForm}>
                <form onSubmit={signInForm.handleSubmit(onSignIn)} className="space-y-4">
                  <FormField
                    control={signInForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">Email</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type="email" 
                              placeholder="you@example.com" 
                              className="pl-10 bg-muted/50 border-border/50 focus:border-primary" 
                              autoComplete="email"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={signInForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">Password</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type={showSignInPassword ? "text" : "password"} 
                              placeholder="••••••••" 
                              className="pl-10 pr-10 bg-muted/50 border-border/50 focus:border-primary" 
                              autoComplete="current-password"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSignInPassword(!showSignInPassword)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                            >
                              {showSignInPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                      </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                  {/* Remember Me Checkbox */}
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="remember-me" 
                      checked={rememberMe}
                      onCheckedChange={(checked) => setRememberMe(checked === true)}
                      className="border-border/50 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                    />
                    <label 
                      htmlFor="remember-me" 
                      className="text-sm text-muted-foreground cursor-pointer select-none"
                    >
                      Remember me
                    </label>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full btn-gaming h-12 text-background font-semibold tracking-wider" 
                    disabled={isLoading}
                  >
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    SIGN IN
                  </Button>
                  
                </form>
              </Form>
              
              {/* Admin Help Request Section */}
              <div className="mt-4 pt-4 border-t border-border/30">
                {!showAdminHelpRequest ? (
                  <Button
                    type="button"
                    onClick={() => setShowAdminHelpRequest(true)}
                    className="w-full text-sm bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 shadow-lg shadow-amber-500/20 transition-all"
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Forgot Password? Request Help from Admin
                  </Button>
                ) : (
                  <div className="space-y-4 p-4 bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-lg border border-amber-500/30">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                        <MessageSquare className="h-5 w-5 text-amber-500" />
                        Password Reset Request
                      </h3>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Enter your name and email. Admin will reset your password and contact you.
                    </p>
                    <RequestPasswordHelp 
                      onSuccess={() => setShowAdminHelpRequest(false)}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowAdminHelpRequest(false)}
                      className="w-full border-amber-500/30 hover:bg-amber-500/10"
                      size="sm"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
              
              {/* Admin Login Hint */}
              {!showAdminHelpRequest && (
                <div className="mt-4 pt-4 border-t border-border/30">
                  <p className="text-xs text-center text-muted-foreground flex items-center justify-center gap-2">
                    <Shield className="h-3 w-3" />
                    Admins will be redirected to the Admin Panel
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="signup">
              <Form {...signUpForm}>
                <form onSubmit={signUpForm.handleSubmit(onSignUp)} className="space-y-4">
                  {/* Avatar Upload - Required */}
                  <div className="flex flex-col items-center mb-4">
                    <div className="relative">
                      <Avatar 
                        className={`w-24 h-24 border-2 cursor-pointer ${avatarError ? 'border-destructive' : avatarFile ? 'border-green-500' : 'border-primary/50'}`} 
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <AvatarImage src={avatarPreview || ''} />
                        <AvatarFallback className="bg-muted text-muted-foreground">
                          <Camera className="h-8 w-8" />
                        </AvatarFallback>
                      </Avatar>
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="absolute bottom-0 right-0 p-1.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                      >
                        <Camera className="h-4 w-4" />
                      </button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                    </div>
                    <p className="text-xs text-center text-muted-foreground mt-2">
                      Profile Picture <span className="text-destructive">*</span>
                    </p>
                    {avatarError && (
                      <p className="text-xs text-destructive mt-1">{avatarError}</p>
                    )}
                  </div>

                  <FormField
                    control={signUpForm.control}
                    name="fullName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">Full Name *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              placeholder="Your name" 
                              className="pl-10 bg-muted/50 border-border/50 focus:border-primary" 
                              autoComplete="name"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={signUpForm.control}
                    name="gameId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">Game ID *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              placeholder="eFootball Game ID" 
                              className="pl-10 bg-muted/50 border-border/50 focus:border-primary" 
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={signUpForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">Email *</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type="email" 
                              placeholder="you@example.com" 
                              className="pl-10 bg-muted/50 border-border/50 focus:border-primary" 
                              autoComplete="email"
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={signUpForm.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground">Phone</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input 
                                {...field} 
                                placeholder="01XXXXXXXXX" 
                                className="pl-10 bg-muted/50 border-border/50 focus:border-primary" 
                                autoComplete="tel"
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={signUpForm.control}
                      name="bloodGroup"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground">Blood Group</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-muted/50 border-border/50">
                                <div className="flex items-center gap-2">
                                  <Droplet className="h-4 w-4 text-muted-foreground" />
                                  <SelectValue placeholder="Select" />
                                </div>
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {bloodGroups.map((group) => (
                                <SelectItem key={group} value={group}>{group}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={signUpForm.control}
                      name="mobileDevice"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground">Device</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input 
                                {...field} 
                                placeholder="iPhone 15" 
                                className="pl-10 bg-muted/50 border-border/50 focus:border-primary" 
                              />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={signUpForm.control}
                      name="district"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground">District</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="bg-muted/50 border-border/50">
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-4 w-4 text-muted-foreground" />
                                  <SelectValue placeholder="Select" />
                                </div>
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {bangladeshDistricts.map((district) => (
                                <SelectItem key={district} value={district}>{district}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={signUpForm.control}
                    name="facebookUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground">Facebook Profile</FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Facebook className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input 
                              {...field} 
                              type="url"
                              placeholder="https://facebook.com/username" 
                              className="pl-10 bg-muted/50 border-border/50 focus:border-primary" 
                            />
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="grid grid-cols-2 gap-4">
                    <FormField
                      control={signUpForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground">Password *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input 
                                {...field} 
                                type={showSignUpPassword ? "text" : "password"} 
                                placeholder="••••••" 
                                className="pl-10 pr-10 bg-muted/50 border-border/50 focus:border-primary" 
                                autoComplete="new-password"
                              />
                              <button
                                type="button"
                                onClick={() => setShowSignUpPassword(!showSignUpPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {showSignUpPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={signUpForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-foreground">Confirm *</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input 
                                {...field} 
                                type={showConfirmPassword ? "text" : "password"} 
                                placeholder="••••••" 
                                className="pl-10 pr-10 bg-muted/50 border-border/50 focus:border-primary" 
                                autoComplete="new-password"
                              />
                              <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                              >
                                {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-gaming h-12 text-background font-semibold tracking-wider" 
                    disabled={isLoading || isUploadingAvatar}
                  >
                    {(isLoading || isUploadingAvatar) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    CREATE ACCOUNT
                  </Button>
                </form>
              </Form>
            </TabsContent>
          </Tabs>
          )}
        </CardContent>
      </Card>
    </div>
    </>
  );
}
